import { useState, useEffect, useRef } from "react";
import {
  Home,
  User,
  Code2,
  FolderGit2,
  GraduationCap,
  Mail,
  Sun,
  Moon,
  Github,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem("darkMode");
      if (savedMode !== null) {
        return savedMode === "true";
      }
    } catch (e) {}
    return true; // Default to dark mode
  });
  const [activeSection, setActiveSection] = useState("home");
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Apply dark mode class and color scheme to html
  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
    try {
      localStorage.setItem("darkMode", darkMode);
    } catch (e) {}
  }, [darkMode]);

  // Handle active section detection on scroll
  useEffect(() => {
    const sections = ["home", "about", "skills", "projects", "education", "contact"];

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollPosition = currentScrollY + window.innerHeight * 0.35;
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  const navItems = [
    { icon: <Home size={19} />, label: t("nav.home"), id: "home" },
    { icon: <User size={19} />, label: t("nav.about"), id: "about" },
    { icon: <Code2 size={19} />, label: t("nav.skills"), id: "skills" },
    { icon: <FolderGit2 size={19} />, label: t("nav.projects"), id: "projects" },
    { icon: <GraduationCap size={19} />, label: t("nav.education"), id: "education" },
    { icon: <Mail size={19} />, label: t("nav.contact"), id: "contact" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      isScrollingRef.current = true;
      setActiveSection(id);
      element.scrollIntoView({ behavior: "smooth" });

      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 700);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "km" : "en";
    i18n.changeLanguage(newLang);
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
  };

  return (
    <>
      {/* Desktop Vertical Sidebar */}
      <nav
        aria-label="Sidebar Navigation"
        className="hidden md:flex fixed top-0 left-0 h-full w-20 flex-col justify-between items-center py-6
                   bg-white/85 dark:bg-black/90 backdrop-blur-xl 
                   border-r border-neutral-200/70 dark:border-neutral-800/80 
                   shadow-[4px_0_30px_rgba(0,0,0,0.03)] dark:shadow-[4px_0_30px_rgba(0,0,0,0.4)] z-50"
      >
        {/* Brand Logo */}
        <button
          onClick={() => scrollToSection("home")}
          className="group relative flex items-center justify-center w-11 h-11 rounded-2xl 
                     bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 
                     shadow-md hover:shadow-lg hover:shadow-blue-500/10 dark:hover:shadow-blue-500/20 
                     transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          aria-label="Rangsey - Back to top"
        >
          <span className="text-lg font-black tracking-tight select-none font-sans">
            R
          </span>
          <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-white dark:ring-black" />
        </button>

        {/* Section Nav Items */}
        <ul className="flex flex-col items-center gap-2 my-auto w-full px-3">
          {navItems.map((item) => {
            const isActive = activeSection === item.id;
            return (
              <li key={item.id} className="relative group w-full flex justify-center">
                <button
                  onClick={() => scrollToSection(item.id)}
                  aria-label={item.label}
                  className={`relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40
                    ${
                      isActive
                        ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-950 shadow-sm font-semibold"
                        : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-900/80"
                    }`}
                >
                  {/* Subtle active indicator pill */}
                  {isActive && (
                    <span className="absolute -left-3 top-1/2 -translate-y-1/2 w-1 h-5 bg-blue-500 rounded-r-full" />
                  )}
                  {item.icon}
                </button>

                {/* Micro Tooltip */}
                <div
                  role="tooltip"
                  className="absolute left-16 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-1.5 transition-all duration-200 z-50 whitespace-nowrap"
                >
                  <div className="px-2.5 py-1 text-xs font-medium rounded-lg shadow-lg bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 border border-neutral-800 dark:border-neutral-200">
                    {item.label}
                  </div>
                </div>
              </li>
            );
          })}

          {/* GitHub External */}
          <li className="relative group w-full flex justify-center pt-2 mt-2 border-t border-neutral-200/60 dark:border-neutral-800/60">
            <a
              href="https://github.com/Rangsey0"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
              className="relative flex items-center justify-center w-11 h-11 rounded-xl text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-900/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
            >
              <Github size={19} />
            </a>
            <div
              role="tooltip"
              className="absolute left-16 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-1.5 transition-all duration-200 z-50 whitespace-nowrap"
            >
              <div className="px-2.5 py-1 text-xs font-medium rounded-lg shadow-lg bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 border border-neutral-800 dark:border-neutral-200">
                GitHub ↗
              </div>
            </div>
          </li>
        </ul>

        {/* Bottom Utility Controls (Language & Theme) */}
        <div className="flex flex-col items-center gap-3 w-full px-3">
          {/* Language Switch */}
          <button
            onClick={toggleLanguage}
            title={i18n.language === "en" ? "ប្តូរទៅជាភាសាខ្មែរ" : "Switch to English"}
            aria-label="Switch Language"
            className="flex items-center justify-center w-10 h-10 rounded-xl 
                       bg-neutral-100 dark:bg-neutral-900/80 hover:bg-neutral-200 dark:hover:bg-neutral-800
                       border border-neutral-200/80 dark:border-neutral-800/80
                       text-[11px] font-bold tracking-wider text-neutral-700 dark:text-neutral-300
                       transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          >
            {i18n.language === "km" ? "KH" : "EN"}
          </button>

          {/* Theme Switch */}
          <button
            onClick={toggleDarkMode}
            title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            aria-label="Toggle Color Theme"
            className="flex items-center justify-center w-10 h-10 rounded-xl
                       bg-neutral-100 dark:bg-neutral-900/80 hover:bg-neutral-200 dark:hover:bg-neutral-800
                       border border-neutral-200/80 dark:border-neutral-800/80
                       text-neutral-700 dark:text-neutral-300
                       transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
          >
            {darkMode ? (
              <Sun size={17} className="text-amber-400 transition-transform duration-300 hover:rotate-45" />
            ) : (
              <Moon size={17} className="text-neutral-800 transition-transform duration-300 hover:-rotate-12" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Floating Bottom Bar */}
      <nav
        aria-label="Mobile Navigation"
        className="md:hidden fixed z-50 left-3 right-3 bottom-4"
      >
        <div className="bg-white/90 dark:bg-neutral-950/90 backdrop-blur-xl border border-neutral-200/80 dark:border-neutral-800/80 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.5)] px-2 py-2">
          <ul className="flex justify-between items-center">
            {navItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    aria-label={item.label}
                    className={`relative flex flex-col items-center justify-center p-2.5 rounded-xl transition-all duration-200
                      ${
                        isActive
                          ? "text-neutral-950 dark:text-white bg-neutral-100 dark:bg-neutral-800/80 font-bold"
                          : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
                      }`}
                  >
                    {item.icon}
                    {isActive && (
                      <span className="absolute bottom-1 w-1 h-1 rounded-full bg-blue-500" />
                    )}
                  </button>
                </li>
              );
            })}

            {/* Language toggle on mobile */}
            <li>
              <button
                onClick={toggleLanguage}
                aria-label="Switch Language"
                className="flex items-center justify-center p-2 rounded-xl text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                <span className="w-7 h-7 flex items-center justify-center text-[10px] font-bold rounded-lg border border-neutral-300 dark:border-neutral-700">
                  {i18n.language === "km" ? "KH" : "EN"}
                </span>
              </button>
            </li>

            {/* Dark mode toggle on mobile */}
            <li>
              <button
                onClick={toggleDarkMode}
                aria-label="Toggle Theme"
                className="flex items-center justify-center p-2.5 rounded-xl text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
              >
                {darkMode ? (
                  <Sun size={19} className="text-amber-400" />
                ) : (
                  <Moon size={19} className="text-neutral-800" />
                )}
              </button>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}
